/**
 * Keep Klaviyo Back In Stock on the PDP buy box only.
 *
 * - Sold Out ATC stays visible (low opacity via CSS).
 * - Empty .klaviyo-bis-trigger is revealed after a delay (default 3s).
 * - Misplaced #klaviyo-bis-button-container (cart Quick Add) is moved into the PDP slot.
 */
(function () {
  const BIS_CONTAINER_ID = 'klaviyo-bis-button-container';
  const IGNORE_SELECTOR = '[data-kv-bis-ignore], quick-add-component, #csk-cart-drawer, .quick-add';
  const SLOT_SELECTOR = '[data-kv-bis-slot]';
  const PDP_FORM_SELECTOR = '[data-kv-pdp-product-form], [data-kv-pdp-buy-buttons]';
  const DEFAULT_DELAY_MS = 3000;

  /** @type {ReturnType<typeof setTimeout> | null} */
  let revealTimer = null;

  function getSlot() {
    return document.querySelector(SLOT_SELECTOR);
  }

  function getDelayMs(slot) {
    const raw = Number(slot?.dataset?.kvBisDelay);
    return Number.isFinite(raw) && raw >= 0 ? raw : DEFAULT_DELAY_MS;
  }

  function isIgnoredContext(node) {
    return Boolean(node && node.closest && node.closest(IGNORE_SELECTOR));
  }

  function isPdpContext(node) {
    return Boolean(node && node.closest && node.closest(PDP_FORM_SELECTOR));
  }

  function clearRevealTimer() {
    if (revealTimer != null) {
      clearTimeout(revealTimer);
      revealTimer = null;
    }
  }

  function hideSlot(slot) {
    if (!slot) return;
    clearRevealTimer();
    slot.hidden = true;
    slot.setAttribute('hidden', '');
    slot.dataset.kvBisSoldOut = 'false';
    document.documentElement.classList.remove('kv-bis-sold-out', 'kv-bis-ready');
  }

  function revealSlot(slot) {
    if (!slot) return;
    slot.hidden = false;
    slot.removeAttribute('hidden');
    document.documentElement.classList.add('kv-bis-ready');
    relocateBisContainer();
  }

  function syncSoldOutUi(isSoldOut) {
    const slot = getSlot();
    if (!slot) return;

    if (!isSoldOut) {
      hideSlot(slot);
      return;
    }

    slot.dataset.kvBisSoldOut = 'true';
    document.documentElement.classList.add('kv-bis-sold-out');
    document.documentElement.classList.remove('kv-bis-ready');

    // Keep slot hidden until delay elapses — Sold Out text stays alone at low opacity.
    slot.hidden = true;
    slot.setAttribute('hidden', '');
    clearRevealTimer();
    revealTimer = setTimeout(() => {
      revealTimer = null;
      if (slot.dataset.kvBisSoldOut !== 'true') return;
      revealSlot(slot);
    }, getDelayMs(slot));
  }

  function relocateBisContainer() {
    const container = document.getElementById(BIS_CONTAINER_ID);
    const slot = getSlot();
    if (!container || !slot) return false;
    if (slot.hasAttribute('hidden')) return false;

    if (slot.contains(container) || isPdpContext(container)) {
      return false;
    }

    if (isIgnoredContext(container) || !isPdpContext(container)) {
      slot.appendChild(container);
      return true;
    }

    return false;
  }

  function onVariantUpdate(event) {
    const resource = event?.detail?.resource;
    if (!resource || resource.available == null) return;

    const sourceId = event?.detail?.sourceId;
    if (sourceId) {
      const sourceEl = document.getElementById(sourceId) || document.querySelector(`[id="${sourceId}"]`);
      if (sourceEl && isIgnoredContext(sourceEl)) return;
    }

    const productId = event?.detail?.data?.productId;
    const slot = getSlot();
    if (productId && slot?.dataset?.productId && String(productId) !== String(slot.dataset.productId)) {
      return;
    }

    const soldOut = resource.available === false;
    syncSoldOutUi(soldOut);

    if (slot && resource.id) {
      slot.dataset.variantId = String(resource.id);
      const trigger = slot.querySelector('.kv-bis-trigger, .klaviyo-bis-trigger');
      if (trigger) trigger.setAttribute('data-variant-id', String(resource.id));
    }
  }

  function init() {
    const slot = getSlot();
    const initiallySoldOut = slot?.dataset?.kvBisSoldOut === 'true';

    if (initiallySoldOut) {
      syncSoldOutUi(true);
    } else {
      hideSlot(slot);
    }

    const observer = new MutationObserver(() => {
      relocateBisContainer();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    document.addEventListener('variant:update', onVariantUpdate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
